import { useState, useEffect } from 'react';
import { getAdministrativeUnitByCode } from '../services/administrativeUnitService';

interface AdministrativeLevelNames {
  [level: string]: string; // level -> name mapping
}

/**
 * Hook to resolve administrative level codes to human-readable names
 * @param levels Administrative levels object with level numbers as keys and codes as values
 * @param countryCode Country code (default: 'ZW')
 * @returns Object with resolved names and loading state
 */
export function useAdministrativeLevelNames(
  levels: Record<string, string> | null | undefined,
  countryCode: string = 'ZW'
): { names: AdministrativeLevelNames; loading: boolean } {
  const [names, setNames] = useState<AdministrativeLevelNames>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!levels || typeof levels !== 'object') {
      setNames({});
      setLoading(false);
      return;
    }

    const resolveNames = async () => {
      const resolved: AdministrativeLevelNames = {};
      const entries = Object.entries(levels);
      
      // Resolve all codes in parallel
      const promises = entries.map(async ([level, code]) => {
        if (!code || typeof code !== 'string') {
          return { level, name: code || 'N/A' };
        }
        
        try {
          const unit = await getAdministrativeUnitByCode(code);
          return { level, name: unit?.adminName || code };
        } catch (error) {
          console.error(`Error resolving admin code ${code}:`, error);
          return { level, name: code }; // Fallback to code
        }
      });

      const results = await Promise.all(promises);
      results.forEach(({ level, name }) => {
        resolved[level] = name;
      });

      setNames(resolved);
      setLoading(false);
    };

    resolveNames();
  }, [levels, countryCode]);

  return { names, loading };
}

